import React, { useState, useEffect } from 'react';
import { FileText, Save, Printer, User, Calendar, Clock, Fuel, Loader2 } from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';

export default function FuelRequisitionForm() {
  const [formData, setFormData] = useState({
    controlNo: '',
    requestorName: '',
    plateNumber: '',
    requestDate: new Date().toISOString().split('T')[0],
    requestTime: new Date().toTimeString().slice(0, 5),
    department: '',
    description: '',
    level: '',
    actualLiters: 0,
    truckOdometer: 0,
    quantityLiters: 0,
    flowMeterBeg: 0,
    flowMeterEnd: 0,
    purpose: '',
    requestedBy: '',
    requestedBySignature: '',
    approvedBy: '',
    approvedBySignature: '',
    notedBy: '',
    notedBySignature: '',
    gasManAssistBy: '',
    gasManSignature: ''
  });

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [currentUserSignature, setCurrentUserSignature] = useState('');

  useEffect(() => {
    fetchData();
    generateControlNo();

    // 🔥 AUTO-SET CURRENT USER FROM LOCALSTORAGE
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

        setFormData(prev => ({
          ...prev,
          requestorName: fullName,
          requestedBy: fullName,
        }));

        // 🔥 AUTO-FETCH SIGNATURE NG CURRENT USER
        if (user.userId) {
          fetchUserSignature(user.userId).then(signature => {
            setCurrentUserSignature(signature);
            setFormData(prev => ({
              ...prev,
              requestedBySignature: signature
            }));
          });
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      // 🔥 FETCH USERS
      const usersRes = await fetch(`${API_BASE_URL}api/Users`);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.filter(u => u.isActive === 1));
        console.log('✅ Users loaded:', usersData.length);
      }

      // 🔥 FETCH DEPARTMENTS
      const deptsRes = await fetch(`${API_BASE_URL}api/Department`);
      if (deptsRes.ok) {
        const deptsData = await deptsRes.json();
        setDepartments(deptsData.filter(d => d.isActive === 1));
        console.log('✅ Departments loaded:', deptsData.length);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load data',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const generateControlNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9000) + 1000;
    setFormData(prev => ({ ...prev, controlNo: `FR-${year}${month}${day}-${random}` }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 🔥 FETCH USER SIGNATURE
  const fetchUserSignature = async (userId) => {
    if (!userId) return '';

    try {
      const signResponse = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);

      if (signResponse.ok) {
        const blob = await signResponse.blob();
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        return base64;
      }
      return '';
    } catch (err) {
      console.warn(`Failed to fetch signature for user ${userId}:`, err);
      return '';
    }
  };

  // 🔥 HANDLE USER SELECTION (para sa ibang dropdowns)
  const handleUserSelect = async (field, userId) => {
    if (!userId) {
      setFormData(prev => ({
        ...prev,
        [field]: '',
        [`${field}Signature`]: ''
      }));
      return;
    }

    const selectedUser = users.find(u => u.id === parseInt(userId));
    if (selectedUser) {
      const signature = await fetchUserSignature(userId);

      setFormData(prev => ({
        ...prev,
        [field]: `${selectedUser.firstName} ${selectedUser.lastName}`,
        [`${field}Signature`]: signature
      }));
    }
  };

const handleSave = async () => {
  if (!formData.requestorName || !formData.plateNumber || !formData.department) {
    Swal.fire({
      icon: 'warning',
      title: 'Missing Information',
      text: 'Please fill in required fields: Requestor, Plate #, and Department',
      confirmButtonColor: '#3b82f6'
    });
    return;
  }

  setLoading(true);
  try {
    const timeSpanFormat = `${formData.requestTime}:00`;

    const payload = {
      id: 0,
      controlNo: formData.controlNo,
      requestorName: formData.requestorName,
      plateNumber: formData.plateNumber,
      requestDate: new Date(formData.requestDate).toISOString(),
      requestTime: timeSpanFormat,
      department: formData.department,
      description: formData.description || null,
      level: formData.level || null,
      actualLiters: parseFloat(formData.actualLiters) || null,
      truckOdometer: parseInt(formData.truckOdometer) || null,
      quantityLiters: parseFloat(formData.quantityLiters) || null,
      flowMeterBeg: parseFloat(formData.flowMeterBeg) || null,
      flowMeterEnd: parseFloat(formData.flowMeterEnd) || null,
      purpose: formData.purpose || null,
      requestedBy: formData.requestedBy || null,
      requestedBySignature: null,
      approvedBy: formData.approvedBy || null,
      approvedBySignature: null,
      notedBy: formData.notedBy || null,
      notedBySignature: null,
      gasManAssistBy: formData.gasManAssistBy || null,
      gasManSignature: null,
      dateCreated: new Date().toISOString()
    };

    console.log('📤 Sending payload:', payload);

    const response = await fetch(`${API_BASE_URL}api/FuelRequisition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Response:', result);

      // ✅ Log successful save
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "CREATE",
          module: "Fuel Requisition",
          description: `Created Fuel Requisition #${formData.controlNo} for ${formData.requestorName} - Plate: ${formData.plateNumber}, Dept: ${formData.department}`,
          userId: user?.id || user?.userId,
          username: user?.username,
          fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username,
        }),
      }).catch((err) => console.error("Log failed:", err));

      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        html: `<strong>${formData.controlNo}</strong><br/>Fuel requisition saved successfully`,
        timer: 2000,
        confirmButtonColor: '#10b981'
      });
      resetForm();
    } else {
      const errorData = await response.json();
      console.error('❌ Error Response:', errorData);
      throw new Error(JSON.stringify(errorData.errors || errorData));
    }
  } catch (error) {
    console.error('❌ Error:', error);

    // ✅ Log failed save
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    await fetch(`${API_BASE_URL}api/Logs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "CREATE-FAILED",
        module: "Fuel Requisition",
        description: `Failed to create Fuel Requisition #${formData.controlNo} for ${formData.requestorName}. Error: ${error.message}`,
        userId: user?.id || user?.userId,
        username: user?.username,
        fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username,
      }),
    }).catch((err) => console.error("Log failed:", err));

    Swal.fire({
      icon: 'error',
      title: 'Save Failed',
      text: error.message || 'Failed to save form',
      confirmButtonColor: '#dc2626'
    });
  } finally {
    setLoading(false);
  }
};

const exportToPDF = () => {
    const doc = new jsPDF();

    // ─── HEADER BACKGROUND (dark blue) ───
    doc.setFillColor(30, 64, 175); // blue-800
    doc.rect(0, 0, 210, 35, 'F');

    // Company Name
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255); // white
    doc.text('MEGASOFT HYGIENIC PRODUCTS, INC.', 105, 13, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(191, 219, 254); // blue-200
    doc.text('A MEGASOFT IN EVERY HOME', 105, 20, { align: 'center' });

    // Document Code Box (right side, white box)
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(255, 255, 255);
    doc.roundedRect(148, 4, 55, 26, 2, 2, 'FD');
    doc.setTextColor(30, 64, 175); // blue-800
    doc.setFontSize(6.5);
    doc.setFont(undefined, 'bold');
    doc.text('DOCUMENT CODE: SPR-F-009', 150, 10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(55, 65, 81); // gray-700
    doc.text('ISSUE NO: 1', 150, 15);
    doc.text('REVISION NO: 0', 150, 19);
    doc.text(`EFFECTIVE DATE: ${new Date().toLocaleDateString()}`, 150, 23);

    // ─── TITLE ───
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 64, 175); // blue-800
    doc.text('FUEL REQUISITION FORM', 105, 44, { align: 'center' });

    // Title underline
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.5);
    doc.line(50, 46, 160, 46);

    let y = 52;

    // ─── INFO SECTION ───
    doc.setDrawColor(148, 163, 184); // gray-400
    doc.setLineWidth(0.3);
    doc.setFillColor(241, 245, 249); // gray-100
    doc.roundedRect(12, y, 186, 38, 2, 2, 'FD');

    // Left column
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105); // gray-600
    doc.text('CONTROL #:', 16, y + 7);
    doc.text('REQUESTOR:', 16, y + 14);
    doc.text('PLATE #:', 16, y + 21);
    doc.text('DEPARTMENT:', 16, y + 28);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42); // near black
    doc.text(formData.controlNo || '—', 45, y + 7);
    doc.text(formData.requestorName || '—', 45, y + 14);
    doc.text(formData.plateNumber || '—', 45, y + 21);
    doc.text(formData.department || '—', 45, y + 28);

    // Right column
    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('DATE:', 120, y + 7);
    doc.text('TIME:', 120, y + 14);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    doc.text(formData.requestDate || '—', 138, y + 7);
    doc.text(formData.requestTime || '—', 138, y + 14);

    y += 44;

    // ─── DETAILS TABLE HEADER ───
    doc.setFillColor(30, 64, 175); // blue-800
    doc.rect(12, y, 186, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.text('DESCRIPTION', 16, y + 5.5);
    doc.text('LEVEL', 68, y + 5.5);
    doc.text('ACTUAL LITERS', 96, y + 5.5);
    doc.text('TRUCK ODOMETER', 138, y + 5.5);

    // Table body
    doc.setFillColor(248, 250, 252); // very light gray
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.rect(12, y + 8, 186, 10, 'FD');

    doc.setTextColor(15, 23, 42);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(formData.description || '—', 16, y + 14);
    doc.text(formData.level || '—', 68, y + 14);
    doc.text(String(formData.actualLiters ?? '—'), 96, y + 14);
    doc.text(String(formData.truckOdometer ?? '—'), 138, y + 14);

    y += 22;

    // Quantity + Flow Meter row
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(148, 163, 184);
    doc.rect(12, y, 186, 16, 'FD');

    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5);
    doc.text('Quantity of Liters Requested:', 16, y + 6);
    doc.text('Flow Meter Beg:', 16, y + 12);
    doc.text('Flow Meter End:', 100, y + 12);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    doc.text(String(formData.quantityLiters ?? '—'), 75, y + 6);
    doc.text(String(formData.flowMeterBeg ?? '—'), 48, y + 12);
    doc.text(String(formData.flowMeterEnd ?? '—'), 130, y + 12);

    y += 22;

    // ─── PURPOSE ───
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(148, 163, 184);
    doc.rect(12, y, 186, 14, 'FD');

    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5);
    doc.text('PURPOSE:', 16, y + 6);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    const purposeLines = doc.splitTextToSize(formData.purpose || '—', 155);
    doc.text(purposeLines, 40, y + 6);

    y += 20;

    // ─── SIGNATURES SECTION HEADER ───
    doc.setFillColor(30, 64, 175);
    doc.rect(12, y, 186, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.text('APPROVALS & SIGNATURES', 105, y + 5, { align: 'center' });

    y += 10;

    // ─── SIGNATURE BOXES - Row 1 ───
    // Requested By box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.rect(12, y, 90, 28, 'FD');

    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('REQUESTED BY:', 16, y + 5);

    // Signature line
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.4);
    doc.line(16, y + 20, 96, y + 20);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    if (formData.requestedBy) {
      doc.text(formData.requestedBy, 16, y + 18);
    }
    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    doc.text("Signature & Date", 16, y + 25);

    // Noted By box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.rect(108, y, 90, 28, 'FD');

    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('NOTED BY:', 112, y + 5);

    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.4);
    doc.line(112, y + 20, 192, y + 20);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    if (formData.notedBy) {
      doc.text(formData.notedBy, 112, y + 18);
    }
    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    doc.text("Signature & Date", 112, y + 25);

    y += 34;

    // ─── SIGNATURE BOXES - Row 2 ───
    // Approved By box (grayed out - N/A)
    doc.setFillColor(226, 232, 240); // slightly darker gray = disabled
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.rect(12, y, 90, 28, 'FD');

    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(148, 163, 184); // muted gray
    doc.text('APPROVED BY:', 16, y + 5);

    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.line(16, y + 20, 96, y + 20);

    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(148, 163, 184);
    doc.text('N/A', 50, y + 14, { align: 'center' });

    doc.setFontSize(6);
    doc.text("Not Required", 16, y + 25);

    // Gas Man/Assist By box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);
    doc.rect(108, y, 90, 28, 'FD');

    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('GAS MAN / ASSIST BY:', 112, y + 5);

    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.4);
    doc.line(112, y + 20, 192, y + 20);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8);
    if (formData.gasManAssistBy) {
      doc.text(formData.gasManAssistBy, 112, y + 18);
    }
    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    doc.text("Signature & Date", 112, y + 25);

    y += 34;

    // ─── FOOTER ───
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 282, 210, 15, 'F');
    doc.setTextColor(191, 219, 254);
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 288, { align: 'center' });
    doc.text('MEGASOFT HYGIENIC PRODUCTS, INC. — Fuel Requisition Form SPR-F-009', 105, 292, { align: 'center' });

    doc.save(`Fuel_Requisition_${formData.controlNo}.pdf`);
};

  const resetForm = () => {
    // 🔥 PRESERVE CURRENT USER DATA AFTER RESET
    const userData = localStorage.getItem('user');
    const user = userData ? JSON.parse(userData) : {};
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

    setFormData({
      controlNo: '',
      requestorName: fullName,              // ← PRESERVE
      plateNumber: '',
      requestDate: new Date().toISOString().split('T')[0],
      requestTime: new Date().toTimeString().slice(0, 5),
      department: '',
      description: '',
      level: '',
      actualLiters: 0,
      truckOdometer: 0,
      quantityLiters: 0,
      flowMeterBeg: 0,
      flowMeterEnd: 0,
      purpose: '',
      requestedBy: fullName,                // ← PRESERVE
      requestedBySignature: currentUserSignature,  // ← PRESERVE SIGNATURE
      approvedBy: '',
      approvedBySignature: '',
      notedBy: '',
      notedBySignature: '',
      gasManAssistBy: '',
      gasManSignature: ''
    });
    generateControlNo();
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-xl shadow-lg">
              <Fuel className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fuel Requisition Form</h1>
              <p className="text-gray-500 mt-1">Document Code: SPR-F-009</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">

          {/* Control No and Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Control # <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="controlNo"
                value={formData.controlNo}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-mono font-bold text-orange-900"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Requestor <span className="text-red-500">*</span>
              </label>
              {/* 🔥 READ-ONLY NA - AUTO GALING SA LOGGED IN USER */}
              <input
                type="text"
                name="requestorName"
                value={formData.requestorName}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-semibold text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Plate # <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="plateNumber"
                value={formData.plateNumber}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., ABC1234"
              />
            </div>
          </div>

          {/* Date, Time, Department */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input
                type="date"
                name="requestDate"
                value={formData.requestDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Time</label>
              <input
                type="time"
                name="requestTime"
                value={formData.requestTime}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.departmentName}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Details Table */}
          <div className="border-2 border-gray-300 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Level</label>
                <input
                  type="text"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., Full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Actual Liters</label>
                <input
                  type="number"
                  name="actualLiters"
                  value={formData.actualLiters}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Truck Odometer</label>
                <input
                  type="number"
                  name="truckOdometer"
                  value={formData.truckOdometer}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity of Liters Requested</label>
              <input
                type="number"
                name="quantityLiters"
                value={formData.quantityLiters}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="0.00"
                step="0.01"
              />
            </div>
          </div>

          {/* Flow Meter */}
          <div className="border-2 border-gray-300 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Flow Meter Beg.</label>
                <input
                  type="number"
                  name="flowMeterBeg"
                  value={formData.flowMeterBeg}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Flow Meter End.</label>
                <input
                  type="number"
                  name="flowMeterEnd"
                  value={formData.flowMeterEnd}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Purpose */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Purpose</label>
            <textarea
              name="purpose"
              value={formData.purpose}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 resize-none"
              placeholder="Enter purpose..."
            />
          </div>

          {/* Signatures / Approvals */}
          <div className="border-2 border-gray-300 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Approvals</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* 🔥 Requested by - READ ONLY NA, AUTO YUNG CURRENT USER */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Requested by</label>
                <input
                  type="text"
                  value={formData.requestedBy}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-semibold text-gray-700"
                />
                {formData.requestedBy && (
                  <p className="mt-2 text-sm font-semibold text-green-700">✓ {formData.requestedBy}</p>
                )}
                {/* 🔥 SHOW SIGNATURE PREVIEW KUNG MAY NAKA-FETCH */}
                {currentUserSignature && (
                  <div className="mt-2 border border-gray-200 rounded-lg p-2 bg-white inline-block">
                    <img
                      src={currentUserSignature}
                      alt="Your Signature"
                      className="h-10 object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Noted By */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Noted By</label>
                <select
                  onChange={(e) => handleUserSelect('notedBy', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {formData.notedBy && (
                  <p className="mt-2 text-sm font-semibold text-green-700">✓ {formData.notedBy}</p>
                )}
              </div>

              {/* Approved by */}
           {/* Approved by - READ ONLY DISPLAY LANG */}
<div className="opacity-50 pointer-events-none">
  <label className="block text-sm font-semibold text-gray-700 mb-2">Approved by</label>
  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-400 font-semibold">
    N/A
  </div>
</div>

              {/* Gas Man/Assist by */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gas Man/Assist by</label>
                <select
                  onChange={(e) => handleUserSelect('gasManAssistBy', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                >
                  <option value="">Select User</option>
                  {users.filter(u => u.authorized?.toLowerCase() === 'gas man').map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {formData.gasManAssistBy && (
                  <p className="mt-2 text-sm font-semibold text-green-700">✓ {formData.gasManAssistBy}</p>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save
              </>
            )}
          </button>

          <button
            onClick={exportToPDF}
            className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2"
          >
            <Printer className="w-5 h-5" />
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}